import { defineSemanticTokens } from '@chakra-ui/react'

// Semantic token overrides. Keep the same names as src/theme/semantic-tokens (bg, bg.panel,
// fg.muted, …) and change only their values, so components pick up the new look unchanged.
//
// Baseline from the Pro LP (components/sco/Pro) and the round 5/6 app mockups:
// - Mostly bare page. Large surfaces (AppLayout panels, cards, containers) are a faint
//   translucent lift over the page, so the page and its ambient light show through; hairlines
//   and long soft shadows define edges instead of fill changes.
// - Overlay surfaces stay opaque: bg.panel (dialogs, menus, popovers), colorPalette.bg (hover
//   cards) and colorPalette.subtle (drawers).
// - Neutral zinc text and lines; orange is the action color (400 solid in dark, near-black text).
// - Light: warm paper (orange.50 page, white panels, sand containers) with neutral gray lines.
// Root colorPalette is `app` (see below): purple surfaces in dark, orange in light, neutral text.

// Every key a palette needs, so `colorPalette="app"` works anywhere a named palette does.
const PALETTE_KEYS = [
  'bg',
  'panel',
  'container',
  'card',
  'contrast',
  'subtle',
  'muted',
  'emphasized',
  'solid',
  'focusRing',
  'border',
]
const SHADES = ['50', '100', '200', '300', '400', '500', '600', '700', '800', '900', '950']
const darkLight = (key: string) => ({
  value: { _light: `{colors.orange.${key}}`, _dark: `{colors.purple.${key}}` },
})

// The root palette. Borrows purple (dark) and orange (light) for surfaces, fills and lines, but
// colorPalette.fg is plain fg: about 1,450 uses of it are everyday text, which the mockups keep
// neutral. Explicit colorPalette="purple" / "orange" stay tinted.
const app = {
  ...Object.fromEntries([...PALETTE_KEYS, ...SHADES].map(key => [key, darkLight(key)])),
  // Both branches, so scoped color modes (Theme) re-resolve it instead of inheriting the root's.
  fg: { value: { _light: '{colors.fg}', _dark: '{colors.fg}' } },
}

export const semanticTokens = defineSemanticTokens({
  colors: {
    app,
    bg: {
      DEFAULT: { value: { _light: '{colors.orange.50}', _dark: '{colors.purple.950}' } },
      emphasized: { value: { _light: '{colors.gray.300}', _dark: '{colors.purple.750}' } },
      panel: { value: { _light: '{colors.white}', _dark: '{colors.purple.900}' } },
      container: { value: { _light: '{colors.sand.50}', _dark: '{colors.purple.800/40}' } },
      // Floating surfaces (menus, popovers, dialogs, select lists, hover cards). Opaque, and in
      // dark a step lighter than bg.panel so they lift off translucent cards. New name: next only.
      overlay: { value: { _light: '{colors.white}', _dark: '{colors.purple.850}' } },
    },
    border: {
      DEFAULT: { value: { _light: '{colors.gray.200}', _dark: '{colors.gray.700/70}' } },
      muted: { value: { _light: '{colors.gray.100}', _dark: '{colors.gray.700/45}' } },
      subtle: { value: { _light: '{colors.gray.50}', _dark: '{colors.gray.700/25}' } },
    },
    gray: {
      // Production pairs black with a .400 fill; solid buttons here fill with gray.solid (600).
      contrast: { value: { _light: '{colors.white}', _dark: '{colors.white}' } },
    },
    purple: {
      panel: { value: { _light: '{colors.purple.100}', _dark: '{colors.purple.900/40}' } },
      container: { value: { _light: '{colors.purple.50}', _dark: '{colors.purple.800/40}' } },
      card: { value: { _light: '{colors.whiteAlpha.500}', _dark: '{colors.purple.900/55}' } },
      subtle: { value: { _light: '{colors.purple.100}', _dark: '{colors.purple.850}' } },
      emphasized: { value: { _light: '{colors.purple.300}', _dark: '{colors.purple.750}' } },
      // Lighter than the LP's line color because the shell draws it at 25–50% alpha.
      border: { value: { _light: '{colors.purple.300}', _dark: '{colors.gray.600}' } },
    },
    orange: {
      bg: { value: { _light: '{colors.orange.50}', _dark: '{colors.purple.950}' } },
      panel: { value: { _light: '{colors.white}', _dark: '{colors.purple.900/40}' } },
      container: { value: { _light: '{colors.sand.50}', _dark: '{colors.purple.800/40}' } },
      card: { value: { _light: '{colors.orange.50}', _dark: '{colors.purple.900/55}' } },
      // Near-black on orange in both modes, like the mockups' CTA (white on orange.600 fails AA).
      contrast: { value: { _light: '{colors.gray.950}', _dark: '{colors.gray.950}' } },
      fg: { value: { _light: '{colors.orange.800}', _dark: '{colors.orange.300}' } },
      subtle: { value: { _light: '{colors.orange.100}', _dark: '{colors.orange.400/12}' } },
      muted: { value: { _light: '{colors.orange.200}', _dark: '{colors.orange.400/20}' } },
      emphasized: { value: { _light: '{colors.orange.300}', _dark: '{colors.orange.400/30}' } },
      solid: { value: { _light: '{colors.orange.600}', _dark: '{colors.orange.400}' } },
      border: { value: { _light: '{colors.gray.300}', _dark: '{colors.orange.400}' } },
    },
    // Ambient page light (globalCss in ./index.ts). Keep these low.
    glow: {
      primary: { value: { _light: '{colors.orange.600/8}', _dark: '{colors.purple.600/22}' } },
      secondary: { value: { _light: '{colors.orange.600/6}', _dark: '{colors.orange.600/10}' } },
    },
  },
  shadows: {
    // Floating surfaces: a hairline ring (no layout cost) plus a long soft drop, like the
    // mockups' toast. New name: next only.
    overlay: {
      value: {
        _light: '0px 0px 0px 1px {colors.gray.900/8}, 0px 16px 40px -16px {colors.gray.900/25}',
        _dark: '0px 0px 0px 1px {colors.whiteAlpha.100}, 0px 18px 40px -12px {colors.black/70}',
      },
    },
    // Long, soft drop shadows (the LP's product frames), no inset highlight.
    lg: {
      value: {
        _light: '0px 16px 40px -20px {colors.gray.900/18}, 0px 0px 1px {colors.gray.900/20}',
        _dark: '0px 16px 40px -20px {black/60}',
      },
    },
    xl: {
      value: {
        _light: '0px 30px 60px -28px {colors.gray.900/22}, 0px 0px 1px {colors.gray.900/20}',
        _dark: '0px 30px 60px -28px {black/75}',
      },
    },
    '2xl': {
      value: {
        _light: '0px 40px 80px -40px {colors.gray.900/28}, 0px 0px 1px {colors.gray.900/20}',
        _dark: '0px 40px 80px -40px {black/80}',
      },
    },
  },
  radii: {
    // Softer corners, per the mockups' 9–10px controls.
    l1: { value: '{radii.sm}' },
    l2: { value: '{radii.lg}' },
    l3: { value: '{radii.xl}' },
  },
})
